import { useCallback, useEffect, useState } from 'react'
import { Bot, Copy, Unplug, KeyRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { apiService, ClientManuelConnecteur, ConnexionConnecteur } from '@/services/api'

/* Le connecteur IA d'UP-CRM, côté administrateur (28/10/2026) : l'adresse à
   donner à Claude ou à Copilot, les connexions en cours (révocables), et la
   création d'un identifiant pour Copilot Studio en mode manuel. */

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })

export default function ConnecteurCard() {
  const { toast } = useToast()
  const [adresse, setAdresse] = useState('')
  const [connexions, setConnexions] = useState<ConnexionConnecteur[]>([])
  const [manuelOuvert, setManuelOuvert] = useState(false)
  const [nom, setNom] = useState('Copilot Studio')
  const [retour, setRetour] = useState('')
  const [cree, setCree] = useState<ClientManuelConnecteur | null>(null)

  const charger = useCallback(async () => {
    const r = await apiService.getConnexionsConnecteur(true)
    if (r.success && r.data) {
      setAdresse(r.data.adresse)
      setConnexions(r.data.connexions)
    }
  }, [])

  useEffect(() => { void charger() }, [charger])

  const copier = (texte: string, quoi: string) => {
    void navigator.clipboard.writeText(texte).then(
      () => toast({ title: `${quoi} copié`, variant: 'success' }),
      () => toast({ title: 'La copie a échoué', description: 'Sélectionnez le texte et copiez-le à la main.', variant: 'destructive' }),
    )
  }

  const revoquer = async (c: ConnexionConnecteur) => {
    const r = await apiService.revoquerConnexionConnecteur(c.id)
    if (r.success) {
      toast({ title: 'Accès retiré', description: `${c.client} n’a plus accès à UP-CRM au nom de ${c.compte}.`, variant: 'success' })
      await charger()
    } else {
      toast({ title: 'L’accès n’a pas pu être retiré', description: r.error, variant: 'destructive' })
    }
  }

  const creerManuel = async () => {
    const r = await apiService.creerClientConnecteur(nom, retour.trim())
    if (r.success && r.data) setCree(r.data)
    else toast({ title: 'L’identifiant n’a pas pu être créé', description: r.error, variant: 'destructive' })
  }

  const fermerManuel = () => {
    setManuelOuvert(false)
    setCree(null)
    setRetour('')
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Bot className="w-5 h-5 text-blue-600" />Connecteur IA (Claude, Copilot)</CardTitle>
        <CardDescription>
          Un assistant IA peut consulter UP-CRM au nom de la personne qui l’y connecte, avec son compte UP-CRM. Tout le monde peut lire ; seuls les administrateurs peuvent créer, modifier et mettre à la corbeille. Le connecteur ne supprime jamais définitivement.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>Adresse du connecteur</Label>
          <div className="flex gap-2">
            <Input readOnly value={adresse} className="font-mono text-sm" />
            <Button variant="outline" onClick={() => copier(adresse, 'L’adresse')} disabled={!adresse}><Copy className="w-4 h-4" /></Button>
          </div>
          <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
            <li>Dans Claude : Paramètres, Connecteurs, « Ajouter un connecteur personnalisé », puis collez cette adresse. Claude ouvre ensuite UP-CRM pour que vous autorisiez l’accès.</li>
            <li>Dans Copilot Studio : Outils, Ajouter un outil, Nouvel outil, « Model Context Protocol », collez l’adresse et choisissez l’authentification OAuth 2.0 en découverte dynamique. Réglez l’outil pour que chaque utilisateur se connecte avec son propre compte.</li>
            <li>Si Copilot Studio exige le mode manuel, créez-lui un identifiant ci-dessous.</li>
          </ul>
          <Button variant="outline" size="sm" onClick={() => setManuelOuvert(true)}>
            <KeyRound className="w-4 h-4 mr-2" />Créer un identifiant pour Copilot Studio (mode manuel)
          </Button>
        </div>

        <div className="space-y-2">
          <Label>Connexions en cours</Label>
          {connexions.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun assistant n’est connecté à UP-CRM pour le moment.</p>
          ) : (
            <div className="border rounded-lg divide-y">
              {connexions.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                  <div>
                    <div className="font-medium">{c.client} <span className="font-normal text-gray-500">au nom de {c.compte}</span></div>
                    <div className="text-xs text-gray-500">Depuis le {formatDate(c.depuis)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={c.ecriture ? 'default' : 'secondary'}>{c.ecriture ? 'Lecture et écriture' : 'Lecture'}</Badge>
                    <Button size="sm" variant="ghost" className="text-red-600" title="Retirer l’accès" onClick={() => void revoquer(c)}>
                      <Unplug className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>

      <Dialog open={manuelOuvert} onOpenChange={(o) => !o && fermerManuel()}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Identifiant pour Copilot Studio</DialogTitle>
            <DialogDescription>
              {cree
                ? 'Copiez ces valeurs dans Copilot Studio maintenant : le secret ne sera plus jamais affiché.'
                : 'Dans Copilot Studio, choisissez l’authentification OAuth 2.0 en mode manuel, créez l’outil, puis collez ici l’adresse de rappel (« callback URL ») qu’il affiche.'}
            </DialogDescription>
          </DialogHeader>
          {cree ? (
            <div className="space-y-3">
              {([
                ['Identifiant du client (Client ID)', cree.client_id],
                ['Secret du client (Client secret)', cree.client_secret],
                ['Adresse d’autorisation (Authorization URL)', cree.autorisation],
                ['Adresse du jeton (Token URL)', cree.jeton],
                ['Adresse de renouvellement (Refresh URL)', cree.renouvellement],
                ['Portées (Scopes)', cree.portees],
              ] as const).map(([libelle, valeur]) => (
                <div key={libelle} className="space-y-1">
                  <Label className="text-xs">{libelle}</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={valeur} className="font-mono text-xs" />
                    <Button variant="outline" size="sm" onClick={() => copier(valeur, libelle)}><Copy className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="cm-nom">Nom</Label>
                <Input id="cm-nom" value={nom} onChange={(e) => setNom(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="cm-retour">Adresse de rappel donnée par Copilot Studio</Label>
                <Input id="cm-retour" placeholder="https://global.consent.azure-apim.net/redirect/…" value={retour} onChange={(e) => setRetour(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            {cree ? (
              <Button onClick={fermerManuel}>J’ai copié les valeurs</Button>
            ) : (
              <>
                <Button variant="outline" onClick={fermerManuel}>Annuler</Button>
                <Button disabled={!nom.trim() || !retour.trim()} onClick={() => void creerManuel()}>Créer l’identifiant</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
